import { z } from 'zod';
import { JOB_TIME_ZONE } from './jobs';

/** Highest start value and next number, far beyond any business use but safe as a JS number. */
export const MAX_SEQUENCE_NUMBER = 999_999_999_999;
export const MAX_SEQUENCE_FORMAT = 40;
/** Digits of `{rand:n}`: fewer than four run out too soon, more than twelve help nobody. */
export const MIN_RANDOM_DIGITS = 4;
export const MAX_RANDOM_DIGITS = 12;
/** Longest number a person may type in by hand. */
export const MAX_MANUAL_SEQUENCE_NUMBER = 40;

const TOKEN = /\{(n(?::(\d{1,2}))?|year|rand:(\d{1,2}))\}/g;
/** Characters allowed around the tokens, e.g. `K-`, `RE-`, `2026/`. */
const LITERAL = /^[A-Za-z0-9 ._/#-]*$/;

type SequencePart =
  string | { token: 'n'; digits: number } | { token: 'year' } | { token: 'rand'; digits: number };

export interface SequenceFormat {
  /** Text and tokens in order, e.g. `K-` and `{n}`. */
  parts: readonly SequencePart[];
  /** The format has `{year}`; its counter starts again each calendar year. */
  yearly: boolean;
  /** The format has `{n}` and takes the next number of a counter. */
  counter: boolean;
  /** The format has `{rand:n}`. */
  random: boolean;
}

/**
 * Reads a number format such as `K-{n}`, `RE-{year}-{n:4}`, `{n:6}` or `P-{rand:6}`. `{n}` and
 * `{rand:n}` appear at most once each and at least one of them; `{n:4}` pads the number with
 * zeros to four digits, `{rand:6}` gives six random digits, `{year}` appears at most once.
 * Returns `null` for anything else.
 */
export function parseSequenceFormat(format: string): SequenceFormat | null {
  if (format.length === 0 || format.length > MAX_SEQUENCE_FORMAT) return null;
  const parts: SequencePart[] = [];
  let numbers = 0;
  let randoms = 0;
  let years = 0;
  let last = 0;
  for (const match of format.matchAll(TOKEN)) {
    const literal = format.slice(last, match.index);
    if (!LITERAL.test(literal)) return null;
    if (literal) parts.push(literal);
    if (match[1] === 'year') {
      years += 1;
      parts.push({ token: 'year' });
    } else if (match[3] !== undefined) {
      randoms += 1;
      const digits = Number(match[3]);
      if (digits < MIN_RANDOM_DIGITS || digits > MAX_RANDOM_DIGITS) return null;
      parts.push({ token: 'rand', digits });
    } else {
      numbers += 1;
      const digits = match[2] === undefined ? 1 : Number(match[2]);
      if (digits < 1 || digits > 12) return null;
      parts.push({ token: 'n', digits });
    }
    last = match.index + match[0].length;
  }
  const rest = format.slice(last);
  if (!LITERAL.test(rest)) return null;
  if (rest) parts.push(rest);
  if (numbers > 1 || randoms > 1 || numbers + randoms === 0 || years > 1) return null;
  return { parts, yearly: years === 1, counter: numbers === 1, random: randoms === 1 };
}

export const sequenceFormatSchema = z
  .string()
  .max(MAX_SEQUENCE_FORMAT)
  .refine((format) => parseSequenceFormat(format) !== null, 'sequence-format');

/** The calendar year in Europe/Berlin, the period of yearly counters. */
export function sequenceYear(at: Date): number {
  return Number(
    new Intl.DateTimeFormat('en', { timeZone: JOB_TIME_ZONE, year: 'numeric' }).format(at),
  );
}

/** The counter a format uses at a time: `''` for one running counter, the year for yearly ones. */
export function sequencePeriod(format: SequenceFormat, at: Date): string {
  return format.yearly ? String(sequenceYear(at)) : '';
}

/** Stands in for `{rand:n}` where no number is given out, e.g. in the preview. */
export function sampleDigits(digits: number): string {
  return '482910375612'.slice(0, digits);
}

/**
 * The formatted number, e.g. `RE-2026-0042`. `random` fills `{rand:n}` with `n` digits: the
 * server passes secure random digits, previews keep the sample.
 */
export function formatSequence(
  format: SequenceFormat,
  value: number,
  at: Date,
  random: (digits: number) => string = sampleDigits,
): string {
  return format.parts
    .map((part) => {
      if (typeof part === 'string') return part;
      if (part.token === 'year') return String(sequenceYear(at));
      if (part.token === 'rand') return random(part.digits);
      return String(value).padStart(part.digits, '0');
    })
    .join('');
}

/** `auto`: the kernel gives the number. `manual`: the person types it in when creating. */
export const SEQUENCE_MODES = ['auto', 'manual'] as const;
export type SequenceMode = (typeof SEQUENCE_MODES)[number];

/**
 * A number typed in by hand, for the input schema of the module's create endpoint: the
 * characters of formats, at least one letter or digit, at most 40 characters.
 */
export const sequenceNumberSchema = z
  .string({ error: 'sequences.numberRequired' })
  .trim()
  .min(1, 'sequences.numberRequired')
  .max(MAX_MANUAL_SEQUENCE_NUMBER, 'sequences.numberInvalid')
  .regex(LITERAL, 'sequences.numberInvalid')
  .regex(/[A-Za-z0-9]/, 'sequences.numberInvalid');

/** A number sequence on the settings page of its module. */
export interface SequenceState {
  key: string;
  /** `manual` when people type the number in; format and next number then rest. */
  mode: SequenceMode;
  /** The module's form takes a typed-in number, so `manual` may be chosen. */
  manualEntry: boolean;
  /** Format the company uses. */
  format: string;
  /** Format from the manifest; differs when the company changed it. */
  defaultFormat: string;
  /** Number the next record gets in the current period of `format`. */
  next: number;
  /** The next record's number as formatted text, with sample digits for `{rand:n}`. */
  preview: string;
}
